/**
 * Citadel — build-time feature flag defaults.
 *
 * This is a STANDALONE copy of the flag manifest. It deliberately duplicates
 * the keys + defaults from workspace/platform-ui/src/lib/features.ts to
 * preserve the operational-independence boundary: Citadel must be able to
 * surface and toggle every platform flag even if platform-ui is offline.
 *
 * Drift discipline: when a new flag is added to platform-ui's features.ts,
 * add it here in the same commit. The Citadel FeatureSwitches surface
 * iterates `BUILD_TIME_FEATURE_DEFAULTS` keys — a missing entry means a
 * flag is invisible to admins on this surface.
 *
 * No Proxy / no runtime-override layer on the Citadel side: Citadel reads
 * override rows directly from /v1/citadel/feature-flags and renders the
 * effective value (override || default) in the toggle UI. There is no
 * "consume features.ts at render time" path inside Citadel itself; the
 * runtime-override layer lives in platform-ui where customer surfaces
 * actually consume the flags.
 *
 * Sprint: SPR-26.O | BL-29-1790
 */
export const BUILD_TIME_FEATURE_DEFAULTS = {
    publisherPayouts: true,
    orbMarketplace: true,
    billingPaymentMethods: true,
    foundingBadge: true,
    embeddedCheckout: true,
    multipleWorkspaces: true,
    workspaceSelector: true,
    workspaceMembers: true,
    workspaceSurfaceConfig: true,
    workspaceBudgets: true,
    apiKeyManagement: true,
    staffDashboard: true,
    eidolonDaemon: false,
    marketplaceSubscribeLive: true,
    lighthouseSimulationView: true,
    simulationHotSwap: false,
    eidolonForecastAdvisor: false,
    simulationCommandPalette: false,
    simulationParticleLayer: false,
    brandCyanLegacy: false,
    scheduleConfigDrawer: true,
    webhookManagerDrawer: true,
    runTriggerCTA: true,
    runsPageV1: true,
    eidolonScheduleAdvisor: false,
    eidolonWebhookAdvisor: false,
    eidolonRunAdvisor: false,
    eidolonRunsAdvisor: false,
    webhookRotateSecretReady: false,
    runInputTemplates: false,
    orbConfigurationsUI: true,
    /**
     * eidolonSmartRec — B2 embedding-rerank scaffold (SPR-26.N-1 / BL-29-1830).
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * Default FALSE — gates the recommendation-context UI explanation layer only.
     * B1 rule-based ranking is always active server-side regardless of this flag.
     */
    eidolonSmartRec: false,
    /**
     * wizardDirectionC — SPR-26.N-1 wizard reframe to dual-pane workbench.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * When ON, the seven wizard routes mount WizardWorkbenchShell instead of
     * the legacy Shell+ContextRail chrome. Default FALSE — flip to TRUE after
     * Phase 2 lands and PD Gate-6 walkthrough signs off on staging.
     */
    wizardDirectionC: false,
    /**
     * inlineConfigEdit — BL-29-2114 / F1 follow-up from reviewer-Opus on `7b50abf1`.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * Gates the Inline Config Edit affordance (staged-edits banner, edit-mode
     * inspector, diff drawer, promote modal, rollback CTA) on the canonical
     * OrbisetDetailPage Composition tab. Default TRUE — Phase 3 full release,
     * PD-ratified 2026-05-21. Kept in lockstep with platform-ui per the Citadel
     * duplication rule; full flag retirement tracked as BL-29-2162.
     */
    inlineConfigEdit: true,
    /**
     * compositionGraphSignsOfLife — BL-29-2237 master gate for the BL-29-2229
     * living composition graph. Mirrors platform-ui/src/lib/features.ts — keep
     * in lockstep per the CLAUDE.md Citadel duplication rule.
     *
     * When ON (default), the orbiset composition canvas renders the full
     * signs-of-life treatment (status glow, breathing, pulse-wave, living
     * edges, flag-gated ambient drift). When OFF — or when `?legacy=true` is
     * appended to any orbiset detail URL — the canvas renders the genuine
     * pre-BL-29-2229 static graph (plain disc + orb_type identity halo only).
     * Default TRUE; the flag exists for PD A/B comparison.
     */
    compositionGraphSignsOfLife: true,
    /**
     * compositionGraphAmbientDrift — BL-29-2229 "signs of life" ambient drift.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * When ON, the orbiset composition graph nodes acquire a very subtle
     * organic position drift. Default FALSE — the most experimental layer of
     * the signs-of-life treatment; gated for a PD walkthrough before release.
     */
    compositionGraphAmbientDrift: false,
    /**
     * orbStatusCanvas — BL-29-2216 single-orb status canvas ("The Solitary
     * Star") on the platform-ui Orb detail page.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * Citadel does NOT mount the Orb detail page or the status canvas; this
     * flag is duplicated here purely to satisfy the standing flag-file parity
     * rule (the two BUILD_TIME_FEATURE_DEFAULTS objects must carry the same
     * key set). Default TRUE — matches platform-ui.
     */
    orbStatusCanvas: true,
    /**
     * runsFilterBarV2 — BL-29-2212 Runs-tab filter bar redesign.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * When ON (default), the orbiset detail Runs tab renders the two-zone
     * <RunsFilterBar> (Filter zone of always-visible Status/Trigger/Time/Target
     * triggers + wrapping chips; View zone of a List/Timeline segmented control
     * + Group: dropdown). When OFF — or with `?legacy=true` on the orbiset
     * detail URL — the legacy <RunsFilterChips> bar renders. Default TRUE.
     *
     * Citadel does NOT mount the orbiset detail page; this flag is duplicated
     * here purely to satisfy the standing flag-file parity rule.
     */
    runsFilterBarV2: true,
    /**
     * aboutModalV2 — BL-29-2256 "About this build" modal redesign.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * When ON (default), the platform-ui VersionPill → "About this build"
     * dialog renders the V2 layout (fixed-height content viewport that fixes
     * the BL-29-2245 tab-switch resize jump, plus an underline tab strip
     * replacing the filled-band segmented switcher). When OFF — or with
     * `?legacy=true` on any URL — the genuine pre-BL-29-2256 AboutModalLegacy
     * renders. Default TRUE.
     *
     * Citadel does NOT mount the AboutModal; this flag is duplicated here
     * purely to satisfy the standing flag-file parity rule (the two
     * BUILD_TIME_FEATURE_DEFAULTS objects must carry the same key set).
     */
    aboutModalV2: true,
    /**
     * nightSkyGraphOverlay — BL-29-2703 Night Sky consumes Capability Graph snapshot.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * When ON (default), platform-ui's `NightSky2D` fetches `/v1/graph/snapshot`
     * and overlays `composes_with` / `implements` edges on top of the
     * deterministic projection. When OFF, the snapshot fetch is skipped and the
     * legacy projection-only star field renders. Default TRUE.
     *
     * Citadel does NOT mount the Observatory; this flag is duplicated here
     * purely to satisfy the standing flag-file parity rule (the two
     * BUILD_TIME_FEATURE_DEFAULTS objects must carry the same key set) AND so
     * Citadel's feature-switches admin surface can toggle the overlay in
     * production without a redeploy if `/v1/graph/snapshot` misbehaves.
     */
    nightSkyGraphOverlay: true,
    /**
     * personalCosmosV1 — BL-29-2742 SPR-26.13 first-pass scaffolding.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * Gates the consumer-facing Personal Cosmos shell on the Observatory
     * Topology → Sky lens (origin-aware empty state + owned-vs-non-owned
     * affordances + filter bar). Default FALSE in production; flipped ON in
     * dev/staging for PD review. Per ADR-027 D1 + D3.
     *
     * Citadel does NOT mount the Observatory; this flag is duplicated here
     * purely to satisfy the standing flag-file parity rule and so Citadel's
     * feature-switches surface can flip the cosmos in production.
     *
     * Sprint: SPR-26.13 | BL-29-2742
     */
    personalCosmosV1: false,
    /**
     * lensSkyConnections — BL-29-2743 SPR-26.13 first-pass scaffolding.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * Gates the collapse of the Topology lens 3-tab sub-lens (`2D · 3D · Graph`)
     * into a 2-tab model (`Sky · Connections`) with internal 2D↔3D toggle.
     * Legacy `?view=2d|3d|graph` URLs continue to resolve via the
     * compatibility table. Default FALSE in production. Per ADR-027 D2.
     *
     * Sprint: SPR-26.13 | BL-29-2743
     */
    lensSkyConnections: false,
    /**
     * eidolonDiscoverV1 — BL-29-2744 SPR-26.13 first-pass scaffolding.
     * Mirrors platform-ui/src/lib/features.ts — keep in lockstep per CLAUDE.md
     * Citadel duplication rule.
     *
     * Gates the persistent bottom-right Eidolon Discover dock (collapsed
     * pill ↔ expanded panel) consuming `POST /eidolon/v1/recommendations`
     * with moat-boundary enforcement (ADR-027 D5). Default FALSE in
     * production; flipped ON in dev/staging for PD review.
     *
     * Sprint: SPR-26.13 | BL-29-2744
     */
    eidolonDiscoverV1: false,
} as const;

export type FeatureFlagKey = keyof typeof BUILD_TIME_FEATURE_DEFAULTS;

export const FEATURE_FLAG_KEYS = Object.keys(
    BUILD_TIME_FEATURE_DEFAULTS,
) as FeatureFlagKey[];
